import { startCashfreePayment, supportedWebGateways } from './cashfreePayment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import PaymentDetail from './PaymentDetail';
import PaymentMethodd from './PaymentMethodd';
import SubscriptionShimmer from './SubscriptionShimmer';
import { applyCoinCoupon, applyExistingCoins, createRazorpayOrder, fetchAvailableCoins, fetchCoinOrderAmounts, removeCoinCoupon, removeExistingCoins } from '../../services/coinPurchaseService';
import { fetchSubjectGateways } from '../../services/subjectSubscriptionService';
import { cartTotals, isEnabled, validCart } from './subjectCart';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../gamification/Coinstore.css';
import '../questionBank/QuestionBankList.css';
import './SelectPaymentMethod.css';
import './SubjectSubscriptions.css';

const emptyCart = [];

function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
}

export default function SubjectSubscriptionPayment() {
  const location = useLocation();
  const navigate = useNavigate();
  const { stateId = 0, profile = {}, discount } = location.state || {};
  const cart = location.state?.subjectCart || emptyCart;
  const selected = cart.filter((item) => item.selected);
  const ids = useMemo(() => selected.map((item) => Number(item.plan.subscription_id)), [cart]); // eslint-disable-line react-hooks/exhaustive-deps
  const names = selected.map((item) => item.plan.subject_name).join(', ');
  const totals = cartTotals(cart);
  const initialAmount = Number(discount?.final_total);
  const order = location.state?.subjectOrder?.vk_order_id ? location.state.subjectOrder : null;
  const [amounts, setAmounts] = useState(null);
  const [gateways, setGateways] = useState(() => supportedWebGateways(location.state?.gateways || []));
  const [selectedGateway, setSelectedGateway] = useState(() => gateways[0] || null);
  const [availableCoins, setAvailableCoins] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [coins, setCoins] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(Boolean(order));
  const [error, setError] = useState('');
  const actionRef = useRef(false);
  const couponForGatewayRef = useRef('');

  const refresh = useCallback(async (orderId, couponCode = '') => {
    const [latestAmounts, latestGateways] = await Promise.all([
      fetchCoinOrderAmounts(orderId), fetchSubjectGateways(ids, couponCode),
    ]);
    if (!Number.isFinite(Number(latestAmounts.final_amount)) || Number(latestAmounts.final_amount) <= 0) throw new Error('The server did not return a payable amount.');
    setAmounts(latestAmounts);
    const supported = supportedWebGateways(latestGateways);
    setGateways(supported);
    setSelectedGateway((current) => supported.find((item) => Number(item.payment_gateway_id) === Number(current?.payment_gateway_id)) || supported[0] || null);
    return { amounts: latestAmounts, gateways: supported };
  }, [ids]);

  useEffect(() => {
    if (!order?.vk_order_id) return undefined;
    let active = true;
    refresh(order.vk_order_id).then(() => {
      fetchAvailableCoins().then((data) => { if (active) setAvailableCoins(data); }).catch(() => {});
      if (active) setLoading(false);
    }).catch((failure) => {
      if (active) { setError(failure.message || 'Unable to load checkout.'); setLoading(false); }
    });
    return () => { active = false; };
  }, [order?.vk_order_id, refresh]);

  const changeOrder = async (request, couponCode) => {
    if (!order?.vk_order_id || actionRef.current) return;
    actionRef.current = true; setBusy(true); setError('');
    try {
      const result = await request();
      const nextCoupon = couponCode === undefined ? (amounts?.discounts?.coupon?.code || '') : couponCode;
      couponForGatewayRef.current = nextCoupon;
      const next = await refresh(order.vk_order_id, nextCoupon);
      setCoupon(next.amounts.discounts?.coupon?.code || '');
      setCoins(next.amounts.discounts?.coins?.coins_used ? String(next.amounts.discounts.coins.coins_used) : '');
      setSuggestions(result.coupons || []);
      fetchAvailableCoins().then(setAvailableCoins).catch(() => {});
    } catch (failure) {
      setSuggestions(failure.backend?.dataset?.data?.coupons || []);
      setError(failure.message || 'Unable to update order.');
    } finally { actionRef.current = false; setBusy(false); }
  };
  const couponApplied = Boolean(amounts?.discounts?.coupon?.code);
  const coinsApplied = Boolean(Number(amounts?.discounts?.coins?.coins_used));
  const handleCoupon = () => {
    if (!couponApplied && !coupon.trim()) return;
    changeOrder(() => couponApplied ? removeCoinCoupon(order.vk_order_id) : applyCoinCoupon(order.vk_order_id, coupon.trim()), couponApplied ? '' : coupon.trim());
  };
  const handleCoins = () => {
    if (!coinsApplied && !/^[1-9]\d*$/.test(coins)) { setError('Enter a positive whole number of coins.'); return; }
    changeOrder(() => coinsApplied ? removeExistingCoins(order.vk_order_id) : applyExistingCoins(order.vk_order_id, coins));
  };
  const retry = async () => {
    if (!order?.vk_order_id) return;
    setError(''); setLoading(true);
    try { await refresh(order.vk_order_id, couponForGatewayRef.current); }
    catch (failure) { setError(failure.message || 'Unable to refresh checkout.'); }
    finally { setLoading(false); }
  };
  const pay = async () => {
    if (!order?.vk_order_id || !selectedGateway || actionRef.current) return;
    actionRef.current = true; setBusy(true); setError('');
    try {
      const latest = await refresh(order.vk_order_id, couponForGatewayRef.current);
      const gateway = latest.gateways.find((item) => Number(item.payment_gateway_id) === Number(selectedGateway.payment_gateway_id));
      if (!gateway) throw new Error('Please select an available payment method.');
      const payable = Number(latest.amounts.final_amount);
      if (['bank-transfer', 'upi-transfer'].includes(gateway.slug)) {
        sessionStorage.setItem('subscriptionPurchasePending', JSON.stringify({ vkOrderId: order.vk_order_id, purchaseType: 'subject', stateId, storePath: SUBSCRIPTION_ROUTES.subjectPlans }));
        navigate(SUBSCRIPTION_ROUTES.manualTransfer, { state: { subjectOrder: order, subjectNames: names, subjectStateId: stateId, paymentGateway: gateway } });
        return;
      }
      if (gateway.slug === 'cashfree') {
        await startCashfreePayment({ orderId: order.vk_order_id, gateway, amount: payable, navigate,
          pendingState: { vkOrderId: order.vk_order_id, purchaseType: 'subject', stateId, storePath: SUBSCRIPTION_ROUTES.subjectPlans } });
        return;
      }
      if (gateway.slug !== 'razorpay' || !gateway.client_id) throw new Error('This payment method is unavailable on web.');
      await loadRazorpay();
      const providerOrder = await createRazorpayOrder(order.vk_order_id, gateway.payment_gateway_id);
      const expectedMinorAmount = Math.round(payable * 100);
      const providerAmount = Number(providerOrder.amount);
      if (Number.isFinite(providerAmount) && providerAmount > 0 && providerAmount !== expectedMinorAmount && providerAmount !== payable) {
        throw new Error('Payment amount does not match the confirmed order amount.');
      }
      const pendingState = { vkOrderId: order.vk_order_id, purchaseType: 'subject', stateId, storePath: SUBSCRIPTION_ROUTES.subjectPlans };
      sessionStorage.setItem('subscriptionPurchasePending', JSON.stringify(pendingState));
      const checkout = new window.Razorpay({
        key: gateway.client_id,
        amount: expectedMinorAmount,
        currency: providerOrder.currency || 'INR',
        order_id: providerOrder.razorpay_order_id,
        name: 'Vidyakasam', description: 'Subject Subscription',
        prefill: { contact: profile.phone || profile.mobile || '', email: profile.email || '' },
        handler: (response) => navigate(SUBSCRIPTION_ROUTES.paymentResult, { state: { ...pendingState, paymentId: response.razorpay_payment_id } }),
        modal: { ondismiss: () => { actionRef.current = false; setBusy(false); } },
      });
      checkout.on('payment.failed', (failure) => { actionRef.current = false; setBusy(false); setError(failure?.error?.description || 'Payment failed.'); });
      checkout.open();
    } catch (failure) { actionRef.current = false; setBusy(false); setError(failure.message || 'Unable to start payment.'); }
  };

  return <><style>{`.homeDashboard-wrapper .page-content { padding-right: 0; } .homeDashboard-wrapper .main-content { height: 100vh; overflow: hidden; box-sizing: border-box; padding-bottom: 35px; min-height: 0; }`}</style><HomeDashboardLayout showHeader={false}><div className="qbl-page qbankList-page page-padding start-gamification coin-payment-page"><div className="start-quiz qbl-main instruction-main">
    <div className="hideProfile-page"><div className="categories-page-header mb-0"><button type="button" className="categories-back-button" onClick={() => navigate(SUBSCRIPTION_ROUTES.subjectDetail, { state: { cart, stateId } })} aria-label="Back to subject details"><i className="bi bi-chevron-left" /></button><h4 className="categories-page-title">Payment Method</h4><HeaderProfileSection /></div></div>
    <div className="coine-balance-wrapper"><div className="rapid-fire-header"><h1 className="qb-title">Payment <span>Method</span></h1></div>
      {!order || !validCart(cart) ? <p role="alert">Select a valid subject plan before checkout. <button type="button" onClick={() => navigate(SUBSCRIPTION_ROUTES.subjectPlans)}>Choose subjects</button></p> : <div className="detail-main-box coin-payment-columns"><section className="qbank-LeftContent">
        <PaymentDetail purchaseType="subject" pack={{ name: names, actual: totals.actual, discounted: initialAmount }} order={order ? { ...order, coupon_discount: amounts?.discounts?.coupon?.discount_amount || 0 } : null} payable={amounts?.final_amount} amountLoading={loading && !amounts} couponCode={coupon} setCouponCode={(code) => { setCoupon(code); setError(''); }} appliedCoupon={amounts?.discounts?.coupon?.code || ''} coins={coins} setCoins={setCoins} appliedCoins={coinsApplied ? coins : ''} availableCoins={availableCoins?.available_coins} conversionLabel={availableCoins?.coins_per_rupee ? `1 rupee = ${availableCoins.coins_per_rupee} coins` : ''} showCoins={isEnabled(availableCoins?.conversion_enabled) || coinsApplied} busy={busy || loading} onCoupon={handleCoupon} onCoins={handleCoins} error={error} suggestions={suggestions} onSuggestion={(code) => { setCoupon(code); changeOrder(() => applyCoinCoupon(order.vk_order_id, code), code); }} onRetry={error && !amounts ? retry : undefined} />
      </section><aside>{loading && !amounts ? <SubscriptionShimmer kind="paymentMethods" /> : <PaymentMethodd gateways={gateways} selectedGateway={selectedGateway} onSelect={setSelectedGateway} onPay={pay} busy={busy || loading} payable={amounts?.final_amount} ready={Boolean(order?.vk_order_id && amounts)} />}</aside></div>}
    </div>
  </div></div></HomeDashboardLayout></>;
}
